import { yupResolver } from "@hookform/resolvers/yup";
import { Delete } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { get } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import SearchAutocomplete from "../../components/SearchAutoComplete";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import clusterService from "../../services/cluster";
import facilityService from "../../services/facility";
import placeService from "../../services/place";
import service from "../../services/property";
import userService from "../../services/users";
import queryDefault from "../../utils/queryDefault";

interface Location {
  x: number,
  y: number
}

interface FormValues {
  name: String,
  is_favorite: boolean,
  cluster_id: number,
  state_id: number,
  city_id: number,
  district_id: number,
  sub_district_id: number,
  owner_id: number,
  type: String,
  park_car: number,
  park_motorcycle: number,
  address: String,
  deposit: number,
  location: any,
  admin_name: string,
  property_facilities: Location[],
  photos: File[],
  description: String
};

export default function PropertyCreate() {
  const notification = useNotification();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const [showCity, setShowCity] = useState(false);
  const [cities, setCities] = useState([]);
  const [showDistrict, setShowDistrict] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [showSubDistrict, setShowSubDistrict] = useState(false);
  const [subDistricts, setSubDistricts] = useState([]);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>({
    lat: -6.1753917,
    lng: 106.8271533,
  });
  const [photos, setPhotos] = useState<File[]>([]);

  const schema = yup.object().shape({
    name: yup.string().min(2).required("Name is a required field and length > 2"),
    cluster_id: yup.number().moreThan(0).required("Cluster is a required field"),
    state_id: yup.number().moreThan(0).required("State is a required field"),
    city_id: yup.number().moreThan(0).required("City is a required field"),
    district_id: yup.number().moreThan(0).required("District is a required field"),
    sub_district_id: yup.number().moreThan(0).required("Sub District is a required field"),
    owner_id: yup.number().moreThan(0).required("Owner is a required field"),
  });

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      is_favorite: false,
      cluster_id: 0,
      state_id: 0,
      city_id: 0,
      district_id: 0,
      sub_district_id: 0,
      owner_id: 0,
      type: "male",
      park_car: 0,
      park_motorcycle: 0,
      address: "",
      deposit: 0,
      location: { x: 0, y: 0 },
      admin_name: user.username,
      property_facilities: [],
      photos: [],
      description: ""
    },
    resolver: yupResolver(schema),
  });

  const handleMarkerDragEnd = (lat: number, lng: number) => {
    setMarkerPosition({ lat, lng });
    form.setValue("location.x", lat);
    form.setValue("location.y", lng);
  };

  const handlePlaceSelected = (
    place: google.maps.places.PlaceResult | null
  ) => {
    if (place) {
      setMarkerPosition({
        lat: place.geometry?.location?.lat() ?? 0,
        lng: place.geometry?.location?.lng() ?? 0,
      });
      form.setValue("location", {
        x: place.geometry?.location?.lat() ?? 0,
        y: place.geometry?.location?.lng() ?? 0,
      });
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    field: keyof FormValues
  ) => {
    if (event.target.files) {
      const fileList = Array.from(event.target.files);
      setImages((prevImages) => [...prevImages, ...fileList]);
      const currentValues = form.getValues(field) as File[];
      form.setValue(field, [...currentValues, ...fileList] as any);
    }
  };

  const handleDelete = (
    index: number,
    setImages: React.Dispatch<React.SetStateAction<File[]>>,
    images: File[],
    field: keyof FormValues
  ) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    form.setValue(field, updatedImages);
  };

  const createProperty = useMutation(
    "create_property",
    service.createProperty,
    {
      onSuccess: () => {
        navigate("/app/properties/property");

        notification.onOpen({
          message: "Property was successfully created!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const listCluster = useQuery("list-cluster", () => clusterService.getAllCluster({ keyword: "" }));

  const listState = useQuery("list-state", () => placeService.getStates({}));

  const listUser = useQuery("list-user", () => userService.getUsers({ keyword: "", is_backoffice: true }));

  const listFacility = useQuery("list-facility", () => facilityService.getAllFacility({ keyword: "", type: "Property" }));

  const onSubmit = (data: any) => {
    if (
      photos.length > 10
    ) {
      notification.onOpen({
        message: "Cannot upload image more than 10",
        type: "error",
        position: "top",
      });
      return;
    }
    const formData = new FormData();
    photos.forEach((photo) => {
      formData.append("property", photo);
    });

    Object.keys(data).forEach((key) => {
      if (!["photos", "location"].includes(key)) {
        formData.append(key, data[key]);
      }
      if (key === "location") {
        formData.append("x", data[key].x);
        formData.append("y", data[key].y);
      }
    });
    createProperty.mutate(formData);
  }

  const [selectedElements, setSelectedElements] = useState<any>([]);

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("property_facilities", value);
    setSelectedElements(value);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Create Property"
        breadcrumbs={[
          { label: "Properties", href: "/app/properties/property" },
          { label: "Create Property", href: "/app/properties/property/create" },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="property"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Cluster</FormLabel>
                <Controller
                  name="cluster_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={0} key={0}>Select Cluster</MenuItem>
                        {get(listCluster, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Is Favorite</FormLabel>
                <Controller
                  name="is_favorite"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Switch
                        onChange={event => {
                          form.setValue("is_favorite", Boolean(event.target.value))
                        }}
                        value={form.getValues("is_favorite")}
                      />
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>State</FormLabel>
                <Controller
                  name="state_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        onChange={event => {
                          form.setValue("state_id", Number(event.target.value));
                          placeService.getCities({ state_id: Number(event.target.value) }).then(response => {
                            setCities(response.data);
                            setShowCity(true);
                            form.setValue("city_id", 0);
                            setShowDistrict(false);
                            form.setValue("district_id", 0);
                            setShowSubDistrict(false);
                            form.setValue("sub_district_id", 0);
                          }).catch(error => {
                            console.log(error)
                          })
                        }}
                      >
                        <MenuItem value={0} key={0}>Select State</MenuItem>
                        {get(listState, ["data", "data"], [])?.map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              {showCity && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>City</FormLabel>
                  <Controller
                    name="city_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("city_id", Number(event.target.value));
                            placeService.getDistricts({ city_id: Number(event.target.value) }).then(response => {
                              setDistricts(response.data);
                              setShowDistrict(true);
                              form.setValue("district_id", 0);
                              setShowSubDistrict(false);
                              form.setValue("sub_district_id", 0);
                            }).catch(error => {
                              console.log(error)
                            })
                          }}
                        >
                          <MenuItem value={0} key={0}>Select City</MenuItem>
                          {cities?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              {showDistrict && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>District</FormLabel>
                  <Controller
                    name="district_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                          onChange={event => {
                            form.setValue("district_id", Number(event.target.value));
                            placeService.getSubDistricts({ district_id: Number(event.target.value) }).then(response => {
                              setSubDistricts(response.data);
                              setShowSubDistrict(true);
                              form.setValue("sub_district_id", 0);
                            }).catch(error => {
                              console.log(error)
                            })
                          }}
                        >
                          <MenuItem value={0} key={0}>Select District</MenuItem>
                          {districts?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              {showSubDistrict && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Sub District</FormLabel>
                  <Controller
                    name="sub_district_id"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Box>
                        <Select
                          {...field}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          fullWidth
                        >
                          <MenuItem value={0} key={0}>Select Sub District</MenuItem>
                          {subDistricts?.map(
                            (item: any, i: any) => (
                              <MenuItem value={item?.id} key={item?.id}>
                                {item?.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </Box>
                    )}
                  />
                </FormControl>
              )}

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Owner</FormLabel>
                <Controller
                  name="owner_id"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={0} key={0}>Select Owner</MenuItem>
                        {get(listUser, ["data", "data"], []).map(
                          (item: any, i: any) => (
                            <MenuItem value={item?.id} key={item?.id}>
                              {item?.name}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={"male"} key={0}>Male Only</MenuItem>
                        <MenuItem value={"female"} key={1}>Female Only</MenuItem>
                        <MenuItem value={"mix"} key={2}>Mix</MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Facility</FormLabel>
                <Controller
                  name="property_facilities"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        multiple
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        renderValue={(selected: any) => selected.map((id: any) => {
                          var facility = ""
                          listFacility?.data?.data?.forEach((element: any) => {
                            if (element.id === id) {
                              facility = element.name
                            }
                          });
                          return facility;
                        }).join(", ")}
                        onChange={onSelectionChange}
                      >
                        {listFacility?.data?.data?.map((facility: any) => (
                          <MenuItem value={facility.id} key={facility.id}>
                            <ListItemText primary={facility.name} />
                            <Checkbox checked={selectedElements.indexOf(facility.id) > -1} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Car Park</FormLabel>
                <Controller
                  name="park_car"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Motorcycle Park</FormLabel>
                <Controller
                  name="park_motorcycle"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Address</FormLabel>
                <Controller
                  name="address"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Deposit</FormLabel>
                <Controller
                  name="deposit"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Location</FormLabel>
                <SearchAutocomplete onPlaceSelected={handlePlaceSelected} />
              </FormControl>
              <Box
                sx={{
                  width: "100%",
                  height: "300px",
                }}
              >
                <Maps
                  center={markerPosition}
                  onMarkerDragEnd={handleMarkerDragEnd}
                  dragable={true}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h3">Photos</Typography>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item lg={6} xs={12}>
                <FormControl fullWidth margin="normal">
                  <Controller
                    name="photos"
                    control={form.control}
                    render={({ field }) => (
                      <>
                        <input
                          type="file"
                          name="property"
                          multiple
                          onChange={(event) =>
                            handleFileChange(
                              event,
                              setPhotos,
                              "photos"
                            )
                          }
                          hidden
                        />
                        <Button
                          variant="contained"
                          component="span"
                          onClick={() => {
                            let element: HTMLElement = document.querySelector(
                              'input[name="property"]'
                            )!;
                            element.click();
                          }}
                        >
                          Upload Photo
                        </Button>
                      </>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={6} xs={12}>
                <List>
                  {photos.map((photo, index) => (
                    <>
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() =>
                              handleDelete(
                                index,
                                setPhotos,
                                photos,
                                "photos"
                              )
                            }
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={photo.name} />
                      </ListItem>
                      <img
                        src={URL.createObjectURL(photo)}
                        alt="Uploaded"
                        style={{
                          width: "100%",
                          borderRadius: "0.375rem",
                          objectFit: "cover",
                          alignSelf: "start"
                        }}
                      />
                    </>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createProperty.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
